import React, { useState, useEffect } from 'react';
import { X, Minimize2, Maximize2, XCircle, CheckCircle, AlertCircle, Database } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useRebuildProgress } from '@/hooks/useRebuildProgress';
import { useAPIClient } from '@/hooks/useAPIClient';

interface Props {
  taskId: string;
  mode: 'all' | 'documents' | 'few-shot';
  onComplete: () => void;
  onClose: () => void;
}

export const RebuildProgressDialog: React.FC<Props> = ({
  taskId,
  mode,
  onComplete,
  onClose,
}) => {
  console.log('[DEBUG] RebuildProgressDialog rendered with taskId:', taskId);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { data: task, isError } = useRebuildProgress(taskId);
  const apiClient = useAPIClient();

  console.log('[DEBUG] Rebuild task data:', task, 'isError:', isError);

  // Handle task completion
  useEffect(() => {
    if (!task) return;

    if (task.status === 'completed') {
      onComplete();
      const timer = setTimeout(onClose, 2000);
      return () => clearTimeout(timer);
    } else if (task.status === 'failed') {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    } else if (task.status === 'cancelled') {
      const timer = setTimeout(onClose, 2000);
      return () => clearTimeout(timer);
    }
  }, [task?.status, onComplete, onClose]);

  const handleCancel = async () => {
    if (isCancelling) return;

    setIsCancelling(true);
    try {
      await apiClient.documents.cancelRebuildTask(taskId);
      // Visual feedback provided by button state and dialog status
    } catch (error) {
      console.error('Failed to cancel rebuild task:', error);
      setIsCancelling(false);
    }
  };

  // Error state
  if (isError) {
    return (
      <Card className="fixed bottom-4 right-4 w-96 p-4 shadow-lg border-destructive z-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <h3 className="font-semibold text-destructive">Fehler</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm">Task konnte nicht geladen werden</p>
      </Card>
    );
  }

  if (!task) return null;

  const modeLabels = {
    'all': 'Vollständiger VDB Neuaufbau',
    'documents': 'Dokumente werden neu indiziert',
    'few-shot': 'Beispiele werden neu indiziert',
  };

  const getStatusIcon = () => {
    switch (task.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <Database className="h-5 w-5 text-primary animate-pulse" />;
    }
  };

  const getStatusBorderClass = () => {
    switch (task.status) {
      case 'completed':
        return 'border-green-500';
      case 'failed':
        return 'border-destructive';
      case 'cancelled':
        return 'border-muted-foreground';
      default:
        return 'border-primary';
    }
  };

  return (
    <Card className={`fixed bottom-4 right-4 w-96 shadow-lg z-50 ${getStatusBorderClass()}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <h3 className="font-semibold">{modeLabels[mode]}</h3>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? (
              <Maximize2 className="h-4 w-4" />
            ) : (
              <Minimize2 className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content (hidden when minimized) */}
      {!isMinimized && (
        <div className="p-4 space-y-3">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fortschritt</span>
              <span className="font-medium">{Math.round(task.progress)}%</span>
            </div>
            <Progress value={task.progress} className="h-2" />
          </div>

          {/* Operations counter */}
          <div className="text-sm">
            <span className="text-muted-foreground">Operationen: </span>
            <span className="font-medium">
              {task.operations_completed} von {task.operations_total}
            </span>
          </div>

          {/* Current operation */}
          {task.current_operation && (
            <div className="text-sm">
              <span className="text-muted-foreground">Aktuelle Operation: </span>
              <span className="font-mono text-xs truncate block mt-1 bg-muted p-2 rounded">
                {task.current_operation}
              </span>
            </div>
          )}

          {/* Error message */}
          {task.error && (
            <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
              <span className="font-semibold">Fehler: </span>
              {task.error}
            </div>
          )}

          {/* Cancel button (only show while running) */}
          {(task.status === 'running' || task.status === 'pending') && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isCancelling}
              className="w-full"
            >
              <XCircle className="h-4 w-4 mr-2" />
              {isCancelling ? 'Wird abgebrochen...' : 'Abbrechen'}
            </Button>
          )}

          {/* Elapsed time */}
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Gestartet: {new Date(task.started_at).toLocaleTimeString('de-DE')}
            {task.completed_at && (
              <> • Beendet: {new Date(task.completed_at).toLocaleTimeString('de-DE')}</>
            )}
          </div>
        </div>
      )}

      {/* Minimized view */}
      {isMinimized && (
        <div className="p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Progress value={task.progress} className="h-1" />
            </div>
            <span className="text-xs font-medium min-w-[3rem] text-right">
              {Math.round(task.progress)}%
            </span>
          </div>
        </div>
      )}
    </Card>
  );
};
